import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMudancas } from '@/hooks/useMudancas';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

const MudancaReports = () => {
  const { mudancas } = useMudancas();

  // Estatísticas por tipo
  const mudancasPorTipo = mudancas.reduce((acc, m) => {
    const tipo = m.tipo_mudanca || 'não definido';
    acc[tipo] = (acc[tipo] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const dadosPorTipo = Object.entries(mudancasPorTipo).map(([tipo, quantidade]) => ({
    tipo,
    quantidade,
  }));

  // Estatísticas por status
  const mudancasPorStatus = mudancas.reduce((acc, m) => {
    const status = m.status || 'não definido';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const dadosPorStatus = Object.entries(mudancasPorStatus).map(([status, quantidade]) => ({
    status,
    quantidade,
  }));

  // Estatísticas por risco
  const mudancasPorRisco = mudancas.reduce((acc, m) => {
    const risco = m.classificacao_risco || 'não definido';
    acc[risco] = (acc[risco] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const dadosPorRisco = Object.entries(mudancasPorRisco).map(([risco, quantidade]) => ({
    risco,
    quantidade,
  }));

  // Taxa de sucesso
  const totalMudancas = mudancas.length;
  const mudancasSucesso = mudancas.filter(m => m.status === 'sucesso').length;
  const mudancasRollback = mudancas.filter(m => m.status === 'rollback_executado').length;
  const mudancasCanceladas = mudancas.filter(m => m.status === 'cancelada').length;
  const mudancasEmAndamento = mudancas.filter(m => 
    m.status === 'em_execucao' || m.status === 'em_avaliacao' || m.status === 'aprovada'
  ).length;

  const taxaSucesso = totalMudancas > 0 ? ((mudancasSucesso / totalMudancas) * 100).toFixed(1) : '0';

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  return (
    <div className="space-y-6">
      {/* Cards de Estatísticas Resumidas */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{totalMudancas}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sucesso</p>
                <p className="text-2xl font-bold text-green-600">{mudancasSucesso}</p>
                <p className="text-xs text-muted-foreground">{taxaSucesso}% taxa</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Em Andamento</p>
                <p className="text-2xl font-bold text-blue-600">{mudancasEmAndamento}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rollback/Canceladas</p>
                <p className="text-2xl font-bold text-red-600">{mudancasRollback + mudancasCanceladas}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Mudanças por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosPorTipo}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="tipo" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantidade" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dadosPorStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ status, quantidade }) => `${status}: ${quantidade}`}
                  outerRadius={80}
                  fill="hsl(var(--primary))"
                  dataKey="quantidade"
                >
                  {dadosPorStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Classificação de Risco</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosPorRisco}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="risco" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="quantidade" fill="hsl(var(--destructive))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Resumo Executivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Taxa de Sucesso</span>
                <Badge variant="default">{taxaSucesso}%</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Mudanças Bem-Sucedidas</span>
                <Badge variant="default">{mudancasSucesso}</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Rollbacks Executados</span>
                <Badge variant="destructive">{mudancasRollback}</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm">Mudanças Canceladas</span>
                <Badge variant="outline">{mudancasCanceladas}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm">Em Andamento</span>
                <Badge variant="secondary">{mudancasEmAndamento}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MudancaReports;
